import type { PartnerClientStatus } from '../../types/aai';

/*
 * The words the partner console uses for time and for a client's lifecycle.
 * Dashboard, Clients, and the client header all read these, so a status can
 * never be labelled two ways on two screens.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const relativeTime = (iso: string | null | undefined): string => {
  if (!iso) return 'No activity yet';
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return 'No activity yet';
  const gap = Date.now() - at;
  if (gap < MINUTE) return 'Just now';
  if (gap < HOUR) return `${Math.floor(gap / MINUTE)} min ago`;
  if (gap < DAY) return `${Math.floor(gap / HOUR)}h ago`;
  if (gap < 2 * DAY) return 'Yesterday';
  if (gap < 14 * DAY) return `${Math.floor(gap / DAY)} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const whenLabel = (iso: string | null | undefined): string => {
  if (!iso) return 'No date set';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'No date set';
  const day = at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${time}`;
};

export const isSoon = (iso: string | null | undefined, hours = 24): boolean => {
  if (!iso) return false;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return false;
  const gap = at - Date.now();
  return gap >= 0 && gap <= hours * HOUR;
};

/*
 * The four lifecycle states, spoken. The chip key picks a tone the kit already
 * has; the label is the word the partner reads. Invited is its own word on a
 * row even though it counts under Prospects in the filters, because "sent and
 * not opened" is a different thing to chase than "never sent".
 *
 * "Prospect", not "Draft". A draft is a half-written record; a prospect is a
 * company you are still winning, which is what this actually is and what the
 * partner is doing about it. The wire value stays `draft`, because renaming a
 * status the server derives buys nothing and breaks every stored filter.
 */
export const STATUS_LABEL: Record<PartnerClientStatus, string> = {
  draft: 'Prospect',
  invited: 'Invited',
  onboarding: 'Onboarding',
  active: 'Active',
};

export const STATUS_CHIP: Record<PartnerClientStatus, string> = {
  draft: 'record_only',
  invited: 'pending',
  onboarding: 'in_progress',
  active: 'active',
};

export const STATUS_EXPLAINED: Record<PartnerClientStatus, string> = {
  draft: 'On your list, not yet invited. No email has gone out.',
  invited: 'The invite is out. Nobody has opened it yet.',
  onboarding: 'The client opened the invite. Their company profile is not fully answered yet.',
  active: 'Every company profile question is answered. Ready for handoff once Orbit has no thin areas left.',
};

/** The Clients filter a status sits under. Invited folds into Prospects. */
export const filterOf = (status: PartnerClientStatus): 'active' | 'draft' | 'onboarding' => {
  if (status === 'invited') return 'draft';
  return status;
};
